import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import {
  CategoriesService,
  type CategoryGroup,
  type CategoryWithGroup,
  type CreateCategoryGroupRequest,
  type CreateCategoryRequest,
} from '../services/categories-service';

const GROUP_TYPE_COLORS: Record<string, string> = {
  income: '#4CAF50',
  expense: '#FF5722',
  transfer: '#9E9E9E',
};

export const CategoriesPage: React.FC = () => {
  const [groups, setGroups] = useState<CategoryGroup[]>([]);
  const [categories, setCategories] = useState<CategoryWithGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CategoryGroup | null>(null);
  const [editingCategory, setEditingCategory] = useState<CategoryWithGroup | null>(null);

  // Form state
  const [groupForm, setGroupForm] = useState<CreateCategoryGroupRequest>({
    name: '',
    type: 'expense',
  });
  const [categoryForm, setCategoryForm] = useState<CreateCategoryRequest>({
    group_id: '',
    name: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupsRes, categoriesRes] = await Promise.all([
        CategoriesService.getGroups(),
        CategoriesService.getCategories(),
      ]);
      setGroups(groupsRes.data.groups);
      setCategories(categoriesRes.data.categories);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCategoriesForGroup = (groupId: string) =>
    categories.filter((c) => c.group_id === groupId);

  // Group CRUD
  const handleCreateGroup = async () => {
    try {
      await CategoriesService.createGroup(groupForm);
      setSuccess('Category group created');
      setGroupDialogOpen(false);
      setGroupForm({ name: '', type: 'expense' });
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to create group');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup) return;
    try {
      await CategoriesService.updateGroup(editingGroup.id, {
        name: groupForm.name,
        type: groupForm.type,
        color: groupForm.color,
        icon: groupForm.icon,
      });
      setSuccess('Category group updated');
      setGroupDialogOpen(false);
      setEditingGroup(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update group');
    }
  };

  const handleDeleteGroup = async (id: string) => {
    if (!confirm('Delete this category group and all its categories?')) return;
    try {
      await CategoriesService.deleteGroup(id);
      setSuccess('Category group deleted');
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete group');
    }
  };

  // Category CRUD
  const handleCreateCategory = async () => {
    try {
      await CategoriesService.createCategory(categoryForm);
      setSuccess('Category created');
      setCategoryDialogOpen(false);
      setCategoryForm({ group_id: '', name: '' });
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;
    try {
      await CategoriesService.updateCategory(editingCategory.id, {
        name: categoryForm.name,
        group_id: categoryForm.group_id,
      });
      setSuccess('Category updated');
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Delete this category?')) return;
    try {
      await CategoriesService.deleteCategory(id);
      setSuccess('Category deleted');
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete category');
    }
  };

  const openEditGroup = (group: CategoryGroup) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      type: group.type,
      icon: group.icon || undefined,
      color: group.color || undefined,
    });
    setGroupDialogOpen(true);
  };

  const openNewGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', type: 'expense' });
    setGroupDialogOpen(true);
  };

  const openEditCategory = (cat: CategoryWithGroup) => {
    setEditingCategory(cat);
    setCategoryForm({
      group_id: cat.group_id,
      name: cat.name,
      icon: cat.icon || undefined,
    });
    setCategoryDialogOpen(true);
  };

  const openNewCategory = (groupId?: string) => {
    setEditingCategory(null);
    setCategoryForm({ group_id: groupId || '', name: '' });
    setCategoryDialogOpen(true);
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Categories
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage transaction categories and groups
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewGroup}>
              New Group
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openNewCategory()}>
              New Category
            </Button>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Chip label={`${groups.length} Groups`} variant="outlined" />
          <Chip label={`${categories.length} Categories`} variant="outlined" />
          <Chip
            label={`${categories.filter((c) => c.group_type === 'income').length} Income`}
            sx={{ bgcolor: '#4CAF5020', color: '#4CAF50', border: '1px solid #4CAF5040' }}
          />
          <Chip
            label={`${categories.filter((c) => c.group_type === 'expense').length} Expense`}
            sx={{ bgcolor: '#FF572220', color: '#FF5722', border: '1px solid #FF572240' }}
          />
          <Chip
            label={`${categories.filter((c) => c.group_type === 'transfer').length} Transfer`}
            sx={{ bgcolor: '#9E9E9E20', color: '#9E9E9E', border: '1px solid #9E9E9E40' }}
          />
        </Box>

        {/* Category groups as accordions */}
        {groups.map((group) => {
          const groupCategories = getCategoriesForGroup(group.id);
          const typeColor = GROUP_TYPE_COLORS[group.type] || '#9E9E9E';

          return (
            <Accordion key={group.id} defaultExpanded={!group.is_system || groupCategories.length < 10}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 2 }}>
                  {group.color && (
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: group.color,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography fontWeight={600} sx={{ flexGrow: 1 }}>
                    {group.name}
                  </Typography>
                  <Chip
                    label={group.type}
                    size="small"
                    sx={{
                      bgcolor: `${typeColor}15`,
                      color: typeColor,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {groupCategories.length} categories
                  </Typography>
                  {group.is_system && (
                    <Chip label="System" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                  )}
                  {!group.is_system && (
                    <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit group">
                        <IconButton size="small" onClick={() => openEditGroup(group)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete group">
                        <IconButton size="small" color="error" onClick={() => handleDeleteGroup(group.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense disablePadding>
                  {groupCategories.map((cat) => (
                    <ListItem key={cat.id} sx={{ py: 0.5, px: 1, borderRadius: 1 }}>
                      <ListItemText
                        primary={cat.name}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <ListItemSecondaryAction>
                        {!cat.is_system && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => openEditCategory(cat)}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteCategory(cat.id)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => openNewCategory(group.id)}
                  sx={{ mt: 1, ml: 1 }}
                >
                  Add Category
                </Button>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Group Dialog */}
        <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingGroup ? 'Edit Category Group' : 'New Category Group'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Name"
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                fullWidth
                autoFocus
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={groupForm.type}
                  label="Type"
                  onChange={(e) =>
                    setGroupForm({ ...groupForm, type: e.target.value as 'income' | 'expense' | 'transfer' })
                  }
                >
                  <MenuItem value="expense">Expense</MenuItem>
                  <MenuItem value="income">Income</MenuItem>
                  <MenuItem value="transfer">Transfer</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Color (hex)"
                value={groupForm.color || ''}
                onChange={(e) => setGroupForm({ ...groupForm, color: e.target.value })}
                placeholder="#FF5733"
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
              disabled={!groupForm.name}
            >
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category Dialog */}
        <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                fullWidth
                autoFocus
              />
              <FormControl fullWidth>
                <InputLabel>Group</InputLabel>
                <Select
                  value={categoryForm.group_id}
                  label="Group"
                  onChange={(e) => setCategoryForm({ ...categoryForm, group_id: e.target.value })}
                >
                  {groups.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {g.color && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: g.color,
                            }}
                          />
                        )}
                        {g.name}
                        <Chip
                          label={g.type}
                          size="small"
                          sx={{ ml: 'auto', height: 18, fontSize: '0.65rem' }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
              disabled={!categoryForm.name || !categoryForm.group_id}
            >
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};
